import React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

// Clean, scrollable product-tour modal. Uses only authentic screen assets
// already present in the repository — no invented or simulated screens.
export default function ProductTour({ tour, onClose, closeRef }) {
  React.useEffect(() => {
    document.body.style.overflow = "hidden";
    closeRef?.current?.focus?.();
    return () => {
      document.body.style.overflow = "";
    };
  }, [closeRef]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") {
        e.preventDefault();
        closeRef?.current?.focus?.();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, closeRef]);

  if (!tour) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-stretch sm:items-center justify-center sm:p-6"
      data-testid="tour-modal"
      role="dialog"
      aria-modal="true"
      aria-label={tour.title}
    >
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-[hsl(var(--z-black))]/70"
        data-testid="tour-backdrop"
      />
      <div className="relative z-10 w-full sm:max-w-[430px] max-h-[100dvh] sm:max-h-[86vh] bg-[hsl(var(--z-surface))] border border-[hsl(var(--z-border-strong))] sm:rounded-3xl flex flex-col overflow-hidden shadow-[0_24px_80px_-24px_hsl(var(--z-teal)/0.3)]">
        <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-[hsl(var(--z-border))] shrink-0">
          <div className="min-w-0">
            <h3
              className="font-display text-[20px] leading-tight text-[hsl(var(--z-text))]"
              data-testid="tour-title"
            >
              {tour.title}
            </h3>
            <p className="mt-0.5 text-[13px] text-[hsl(var(--z-text-soft))]">
              {tour.caption}
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close product tour"
            data-testid="tour-close"
            className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full border border-[hsl(var(--z-border-strong))] text-[hsl(var(--z-text))] hover:border-[hsl(var(--z-teal))] transition-colors"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div
          className="flex-1 overflow-y-auto overscroll-contain px-5 py-5"
          data-testid="tour-scroll"
        >
          {tour.notice && (
            <p
              className="mb-4 text-center text-[12px] uppercase tracking-[0.16em] text-[hsl(var(--z-teal))]/70"
              data-testid="tour-notice"
            >
              {tour.notice}
            </p>
          )}
          <img
            src={tour.image}
            alt={`${tour.title} — ${tour.caption}`}
            width={tour.width}
            height={tour.height}
            loading="eager"
            decoding="async"
            className="w-full h-auto rounded-2xl"
            data-testid={`tour-image-${tour.id}`}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
